import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {CircularProgress, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Collapse, Checkbox, FormControlLabel, FormControl, InputLabel, Select, MenuItem} from "@material-ui/core";
import DrawerMenuRight from "../Common/DrawerMenuRight";
import {ExpandMore, ExpandLess} from '@material-ui/icons';
import axios from 'axios';
import {ServiceLocator} from "../../Services/ServiceLocator";
import Autocomplete from "@material-ui/lab/Autocomplete";
import WorkshopTemplate from "./WorkshopTemplate";
import { AuthenticationService } from "../../Services/AuthenticationService";
import { customerOrderService } from "../../Services/CustomerOrderService";
import { COUNTRIES, US_STATES, isUnitedStates, normalizeCountryCode, normalizeUsStateCode } from '../../utils/geo';
import { getNumberInputDisplayValue, parseNumberInputValue } from '../../utils/numberInputUtils';
import { formatUtcForDisplay } from '../../utils/dateUtils';
import { getPackingSlipItemCount, isPackingSlipFullyReceived } from '../../utils/packingSlipShipmentUtils';

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
});

// Helper functions for determining currently selling assembly
const getCurrentlySellingAssemblyId = (assemblyId, assemblies) => {
    // Get all assemblies with the same assemblyId that don't have a finalOrderNumber
    const assembliesWithoutFinalOrder = assemblies.filter(assembly => 
        assembly.assembly.assemblyId === assemblyId && 
        !assembly.assembly.finalOrderNumber
    );
    
    // If only one assembly without finalOrderNumber, it's the currently selling one
    if (assembliesWithoutFinalOrder.length === 1) {
        return assembliesWithoutFinalOrder[0].assembly.id;
    }
    
    // If multiple assemblies without finalOrderNumber, find the one with the lowest revision
    if (assembliesWithoutFinalOrder.length > 1) {
        // Sort by revision (ascending) and return the lowest
        const sortedAssemblies = assembliesWithoutFinalOrder.sort((a, b) => {
            const revisionA = a.assembly.revision || '';
            const revisionB = b.assembly.revision || '';
            return revisionA.localeCompare(revisionB, undefined, { numeric: true, sensitivity: 'base' });
        });
        return sortedAssemblies[0].assembly.id;
    }
    
    // If no assemblies without finalOrderNumber, return null
    return null;
};

const isAssemblyCurrentlySelling = (assembly, assemblies) => {
    if (!assembly || assembly.finalOrderNumber) {
        return false;
    }
    
    const currentlySellingId = getCurrentlySellingAssemblyId(assembly.assemblyId, assemblies);
    return currentlySellingId === assembly.id;
};

const buildWorkshopOption = ({ title, value, description }) => ({
    title,
    value,
    description: description || ''
});

const samePackingSlipId = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

const getBoxReceiveStatus = (box) => {
    const totalExpected = box.items?.reduce((sum, item) => sum + (item.quantityExpected || 0), 0) || 0;
    const totalReceived = box.items?.reduce((sum, item) => sum + (item.quantityReceived || 0), 0) || 0;
    const remainingToReceive = Math.max(0, totalExpected - totalReceived);
    return {
        totalExpected,
        totalReceived,
        remainingToReceive,
        isPartiallyReceived: totalReceived > 0 && remainingToReceive > 0,
        isFullyReceived: isPackingSlipFullyReceived(box)
    };
};

let todaysChanges;
let assemblyFamilies;

class Search extends React.Component {
    state = {
        loading: true,
        user: {},
        users: [],
        orders: [],
        pendingCustomerOrders: [],
        customerOrderFulfillOpen: false,
        selectedCustomerOrder: null,
        customerOrderShippingForm: {
            customerName: '',
            line1: '',
            line2: '',
            city: '',
            stateOrProvince: '',
            postalCode: '',
            country: 'US',
        },
        customerOrderAutoPackage: true,
        customerOrderWeightOz: 16,
        customerOrderFulfilling: false,
        reloads: [],
        transactions: [],
        packingSlips: null,
        selectedPackingSlip: null,
        receivedQuantities: {},
        receivingPackingSlip: false,
        // New state for expand/collapse functionality
        collapsedSections: {
            inventory: true,
            packaged: true,
            sales: true,
            received: true
        },
        // Multi-box shipment receiving state
        selectedMultiBoxShipment: null,
        // Part lookup state
        outstandingParts: [],
        parts: [],
        partFamilies: []
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;

        this._packingSlipsLoadId = 0;
        this._locallyReceivedPackingSlipIds = new Set();
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Workshop')) {
            globalProps.history.push('/login');
            return;
        }

        Promise.all([self.loadParts(), self.loadAssemblies(), self.loadSubAssemblies(), self.loadAssemblyFamilies(), self.loadTodaysChanges(), self.loadPendingCustomerOrders()]).then(() => {
            self.setState({loading: false, user, permissions: user.userPermissions, todaysChanges});

            self.loadPackingSlips();

            let assemblyOptions = [];

            this.state.assemblyFamilies.forEach((family) => {
                assemblyOptions.push(buildWorkshopOption({
                    title: `${family.assemblyFamilyId} 👨‍👩‍👦`,
                    value: family.id,
                    description: family.description
                }));
            });
            this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((assembly) => {
                assemblyOptions.push(buildWorkshopOption({
                    title: `${assembly.subAssembly.subAssemblyId} rev ${assembly.subAssembly.revision}${assembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                    value: assembly.subAssembly.id,
                    description: assembly.subAssembly.description
                }));
            });
            this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
                assemblyOptions.push(buildWorkshopOption({
                    title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}${assembly.assembly.currentlyPackaging ? ' 📦' : ''}${isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies) ? ' 🛒' : ''}`,
                    value: assembly.assembly.id,
                    description: assembly.assembly.description
                }));
            });
            this.state.parts.filter(x => x.part.isActive).forEach((assembly) => {
                assemblyOptions.push(buildWorkshopOption({
                    title: `${assembly.part.partId} rev ${assembly.part.revision}`,
                    value: assembly.part.id,
                    description: assembly.part.description
                }));
            });

            const partOrAssembly = globalProps.match.params.partOrAssembly;
            if (partOrAssembly) self.handleAssemblyChange(null, assemblyOptions.find(x => x.value === partOrAssembly));
        });
    };

    loadPendingCustomerOrders = () => {
        return customerOrderService.getPending()
            .then((orders) => this.setState({ pendingCustomerOrders: orders || [] }))
            .catch(() => this.setState({ pendingCustomerOrders: [] }));
    };

    openCustomerOrderFulfill = (order) => {
        customerOrderService.getById(order.id)
            .then((detail) => this.setState({
                customerOrderFulfillOpen: true,
                selectedCustomerOrder: detail,
                customerOrderShippingForm: this.buildCustomerOrderShippingForm(detail),
            }))
            .catch(() => window.$alertError('Failed to load order details'));
    };

    buildCustomerOrderShippingForm = (order) => {
        const country = normalizeCountryCode(order?.shippingCountry) || 'US';
        return {
            customerName: order?.customerName || '',
            line1: order?.shippingLine1 || '',
            line2: order?.shippingLine2 || '',
            city: order?.shippingCity || '',
            stateOrProvince: isUnitedStates(country) ? (normalizeUsStateCode(order?.shippingState) || '') : '',
            postalCode: order?.shippingPostalCode || '',
            country,
        };
    };

    setCustomerOrderShippingField = (field, value) => {
        this.setState((prev) => {
            const customerOrderShippingForm = { ...prev.customerOrderShippingForm, [field]: value };
            if (field === 'country' && !isUnitedStates(value)) customerOrderShippingForm.stateOrProvince = '';
            return { customerOrderShippingForm };
        });
    };

    saveCustomerOrderShippingAddress = () => {
        const { selectedCustomerOrder, customerOrderShippingForm } = this.state;
        if (!selectedCustomerOrder) return;
        customerOrderService.updateShippingAddress(selectedCustomerOrder.id, customerOrderShippingForm)
            .then((detail) => this.setState({
                selectedCustomerOrder: detail,
                customerOrderShippingForm: this.buildCustomerOrderShippingForm(detail),
            }))
            .catch((err) => window.$alertError(err?.message || 'Failed to save shipping address'));
    };

    closeCustomerOrderFulfill = () => {
        this.setState({
            customerOrderFulfillOpen: false,
            selectedCustomerOrder: null,
        });
    };

    fulfillCustomerOrder = () => {
        const { selectedCustomerOrder, customerOrderAutoPackage, customerOrderWeightOz } = this.state;
        if (!selectedCustomerOrder) return;
        this.setState({ customerOrderFulfilling: true });
        customerOrderService.fulfill(selectedCustomerOrder.id, {
            autoPackage: customerOrderAutoPackage,
            packages: [{
                weightOz: Number(customerOrderWeightOz) || 16,
                carrierCode: selectedCustomerOrder.shippingCarrierCode,
                serviceCode: selectedCustomerOrder.shippingServiceCode,
            }],
        })
            .then((order) => {
                this.setState({ customerOrderFulfilling: false });
                this.closeCustomerOrderFulfill();
                this.loadPendingCustomerOrders();
                const labelUrl = order.shipments && order.shipments.length > 0
                    ? order.shipments[order.shipments.length - 1].labelPdfUrl
                    : null;
                if (labelUrl) window.open(labelUrl, '_blank');
                window.$alertSuccess(`Order ${order.orderNumber} fulfilled`);
            })
            .catch((err) => {
                this.setState({ customerOrderFulfilling: false });
                window.$alertError(err?.message || 'Fulfillment failed');
            });
    };

    loadParts = () => {
        const self = this;
        self.setState({loading: true});
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPartsExpanded().then((parts) => {
            parts = parts.sort(function (a, b) {
                if (a.part.partId > b.part.partId) return 1;
                if (b.part.partId > a.part.partId) return -1;
                
                // Finally by revision (descending)
                if (a.part.revision !== b.part.revision) {
                    if (a.part.revision > b.part.revision) return -1;
                    if (a.part.revision < b.part.revision) return 1;
                }
                
                return 0;
            });
            self.setState({parts});
        });
    };

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllAssembliesExpanded().then((assemblies) => {
            assemblies = assemblies.sort(function (a, b) {
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                if (b.assembly.assemblyId > a.assembly.assemblyId) return -1;
                
                // Finally by revision (descending)
                if (a.assembly.revision !== b.assembly.revision) {
                    if (a.assembly.revision > b.assembly.revision) return -1;
                    if (a.assembly.revision < b.assembly.revision) return 1;
                }
                
                return 0;
            });
            self.setState({assemblies});
        });
    };

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllSubAssembliesExpanded().then((subAssemblies) => {
            subAssemblies = subAssemblies.sort(function (a, b) {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                
                // Finally by revision (descending)
                if (a.subAssembly.revision !== b.subAssembly.revision) {
                    if (a.subAssembly.revision > b.subAssembly.revision) return -1;
                    if (a.subAssembly.revision < b.subAssembly.revision) return 1;
                }
                
                return 0;
            });
            self.setState({subAssemblies});
        });
    };

    loadAssemblyFamilies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAssemblyFamilies().then((assemblyFamiliesResult) => {
            assemblyFamilies = assemblyFamiliesResult.sort(function (a, b) {
                if (a.assemblyFamilyId > b.assemblyFamilyId) return 1;
                if (b.assemblyFamilyId > a.assemblyFamilyId) return -1;
                
                return 0;
            });
            self.setState({assemblyFamilies});
        });
    };

    loadTodaysChanges = () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getTodaysChanges().then((todaysChangesResult) => {
            todaysChanges = todaysChangesResult;
        });
    };

    loadPackingSlips = () => {
        const partService = ServiceLocator.Instance.getPartService();
        const loadId = ++this._packingSlipsLoadId;
        return partService.getAllPackingSlips().then((packingSlips) => {
            if (loadId !== this._packingSlipsLoadId) {
                return null;
            }

            const activePackingSlips = packingSlips.filter(ps =>
                !isPackingSlipFullyReceived(ps)
                && getPackingSlipItemCount(ps) > 0
                && !this._locallyReceivedPackingSlipIds.has(String(ps.id).toLowerCase())
            );
            activePackingSlips.sort((a, b) => {
                const dateA = a.dateCreated ? new Date(a.dateCreated) : new Date(0);
                const dateB = b.dateCreated ? new Date(b.dateCreated) : new Date(0);
                return dateA - dateB;
            });

            return new Promise((resolve) => {
                this.setState({packingSlips: activePackingSlips}, () => resolve(activePackingSlips));
            });
        });
    };

    loadAllPurchasedPartsForVendor = (vendorName) => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        return partService.getAllPurchasedPartsByVendor(vendorName).then((allPurchasedParts) => {
            self.setState({outstandingParts: allPurchasedParts});
        });
    };

         handlePackingSlipSelect = (packingSlip) => {
         const receivedQuantities = {};
         if (packingSlip.items) {
             packingSlip.items.forEach(item => {
                 // For partially received items, show the remaining quantity to receive
                 const remainingToReceive = Math.max(0, item.quantityExpected - (item.quantityReceived || 0));
                 receivedQuantities[item.id] = remainingToReceive;
             });
         }
         // Load outstanding parts for this vendor
         this.loadAllPurchasedPartsForVendor(packingSlip.vendorName);
         this.setState({
             selectedPackingSlip: packingSlip,
             receivedQuantities
         });
     };

    handleReceiveAll = async () => {
        this.setState({receivingPackingSlip: true});
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const packingSlip = this.state.selectedPackingSlip;
            const packingSlipId = packingSlip.id;

            const totalReceived = packingSlip.items?.reduce((sum, item) => sum + (item.quantityReceived || 0), 0) || 0;
            const isFullyUnreceived = totalReceived === 0;

            if (isFullyUnreceived) {
                await partService.receivePurchaseOrderPackingSlip(packingSlipId);
            } else {
                const remainingQuantities = {};
                packingSlip.items?.forEach(item => {
                    const remainingToReceive = Math.max(0, item.quantityExpected - (item.quantityReceived || 0));
                    if (remainingToReceive > 0) {
                        remainingQuantities[item.id] = remainingToReceive;
                    }
                });
                await partService.receivePackingSlipWithQuantities(packingSlipId, remainingQuantities);
            }

            window.$alertSuccess('Packing slip received successfully');
            this.setState({
                selectedPackingSlip: null,
                receivedQuantities: {}
            });
            await this.afterPackingSlipFullyReceived(packingSlipId);
        } catch (error) {
            console.error('Error receiving packing slip:', error);
            if (error.message && error.message.includes('already been received')) {
                window.$alertError("This packing slip has already been received");
            } else {
                window.$alertError('Failed to receive packing slip');
            }
        } finally {
            this.setState({receivingPackingSlip: false});
        }
    };

    handleReceiveCustom = async () => {
        this.setState({receivingPackingSlip: true});
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const packingSlip = this.state.selectedPackingSlip;
            const packingSlipId = packingSlip.id;
            const receivedQuantities = { ...this.state.receivedQuantities };

            await partService.receivePackingSlipWithQuantities(packingSlipId, receivedQuantities);

            const fullyReceived = (packingSlip.items || []).every(item => {
                const newlyReceived = receivedQuantities[item.id] || 0;
                return (item.quantityReceived || 0) + newlyReceived >= (item.quantityExpected || 0);
            });

            window.$alertSuccess('Packing slip received successfully');
            this.setState({
                selectedPackingSlip: null,
                receivedQuantities: {}
            });

            if (fullyReceived) {
                await this.afterPackingSlipFullyReceived(packingSlipId);
            } else {
                this.applyPartialReceiveToOpenShipment(packingSlipId, receivedQuantities);
                const packingSlips = await this.loadPackingSlips();
                if (packingSlips) {
                    this.syncOpenMultiBoxShipment(packingSlips);
                }
            }
        } catch (error) {
            console.error('Error receiving packing slip:', error);
            if (error.message && error.message.includes('already been received')) {
                window.$alertError("This packing slip has already been received");
            } else {
                window.$alertError('Failed to receive packing slip');
            }
        } finally {
            this.setState({receivingPackingSlip: false});
        }
    };

    handleQuantityChange = (itemId, value) => {
        this.setState(prevState => ({
            receivedQuantities: {
                ...prevState.receivedQuantities,
                [itemId]: parseNumberInputValue(value)
            }
        }));
    };

    closePackingSlipDialog = () => {
        this.setState({
            selectedPackingSlip: null,
            receivedQuantities: {}
        });
    };

    handleReceiveAllDirect = async (packingSlip) => {
        this.setState({receivingPackingSlip: true});
        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.receivePurchaseOrderPackingSlip(packingSlip.id);

            window.$alertSuccess('Packing slip received successfully');
            await this.afterPackingSlipFullyReceived(packingSlip.id);
        } catch (error) {
            console.error('Error receiving packing slip:', error);
            if (error.message && error.message.includes('already been received')) {
                window.$alertError("This packing slip has already been received");
            } else {
                window.$alertError('Failed to receive packing slip');
            }
        } finally {
            this.setState({receivingPackingSlip: false});
        }
    };

    afterPackingSlipFullyReceived = async (packingSlipId) => {
        this._locallyReceivedPackingSlipIds.add(String(packingSlipId).toLowerCase());
        this.markBoxFullyReceivedInOpenShipment(packingSlipId);
        const packingSlips = await this.loadPackingSlips();
        if (packingSlips) {
            this.syncOpenMultiBoxShipment(packingSlips);
        }
    };

    markBoxFullyReceivedInOpenShipment = (packingSlipId) => {
        const shipment = this.state.selectedMultiBoxShipment;
        if (!shipment) return;

        const updatedBoxes = shipment.boxes.map(box => {
            if (!samePackingSlipId(box.id, packingSlipId)) return box;
            const status = getBoxReceiveStatus({
                ...box,
                items: box.items?.map(item => ({
                    ...item,
                    quantityReceived: item.quantityExpected
                })) || []
            });
            return {
                ...box,
                items: box.items?.map(item => ({
                    ...item,
                    quantityReceived: item.quantityExpected
                })) || [],
                ...status,
                isFullyReceived: true,
                isPartiallyReceived: false,
                remainingToReceive: 0
            };
        });

        if (updatedBoxes.every(box => box.isFullyReceived)) {
            this.closeMultiBoxShipmentDialog();
            return;
        }

        this.setState({
            selectedMultiBoxShipment: {
                ...shipment,
                boxes: updatedBoxes
            }
        });
    };

    applyPartialReceiveToOpenShipment = (packingSlipId, receivedQuantities) => {
        const shipment = this.state.selectedMultiBoxShipment;
        if (!shipment) return;

        const updatedBoxes = shipment.boxes.map(box => {
            if (!samePackingSlipId(box.id, packingSlipId)) return box;
            const updatedItems = box.items?.map(item => {
                const itemReceived = receivedQuantities[item.id] || 0;
                return {
                    ...item,
                    quantityReceived: (item.quantityReceived || 0) + itemReceived
                };
            }) || [];
            return {
                ...box,
                items: updatedItems,
                ...getBoxReceiveStatus({ ...box, items: updatedItems })
            };
        });

        this.setState({
            selectedMultiBoxShipment: {
                ...shipment,
                boxes: updatedBoxes
            }
        });
    };

    buildShipmentFromBoxes = (shipment, boxes) => {
        const boxesWithStatus = boxes.map(box => ({
            ...box,
            ...getBoxReceiveStatus(box)
        }));
        boxesWithStatus.sort((a, b) => {
            const aBoxNum = parseInt(a.name.match(/Box\s(\d+)$/)?.[1] || 0);
            const bBoxNum = parseInt(b.name.match(/Box\s(\d+)$/)?.[1] || 0);
            return aBoxNum - bBoxNum;
        });
        return {
            ...shipment,
            boxes: boxesWithStatus,
            totalItems: boxesWithStatus.reduce((sum, box) => sum + (box.items?.length || 0), 0),
            isFullyReceived: boxesWithStatus.every(box => box.isFullyReceived),
            hasAnyReceiving: boxesWithStatus.some(box => box.isPartiallyReceived || !box.isFullyReceived)
        };
    };

    syncOpenMultiBoxShipment = (packingSlips) => {
        const shipment = this.state.selectedMultiBoxShipment;
        if (!shipment) return;

        const remainingBoxes = packingSlips.filter(slip => {
            if (this._locallyReceivedPackingSlipIds.has(String(slip.id).toLowerCase())) {
                return false;
            }
            if (getPackingSlipItemCount(slip) === 0) {
                return false;
            }
            const match = slip.name && slip.name.match(/^(.+)\s-\sBox\s\d+$/);
            return match && match[1] === shipment.baseName;
        });

        if (remainingBoxes.length === 0) {
            this.closeMultiBoxShipmentDialog();
            return;
        }

        this.setState({
            selectedMultiBoxShipment: this.buildShipmentFromBoxes(shipment, remainingBoxes)
        });
    };

    handleAssemblyChange = (event, newValue) => {
        this.setState({selectedPartOrAssembly: !newValue ? null : newValue.value});
    };

         // Multi-box shipment detection and grouping (copied from PackingSlips.js)
     detectMultiBoxShipments = (packingSlips) => {
         const shipmentGroups = {};
         const multiBoxShipments = [];
         const standaloneSlips = [];
         
         packingSlips.forEach(slip => {
             if (getPackingSlipItemCount(slip) === 0) {
                 return;
             }
             const baseNameMatch = slip.name.match(/^(.+)\s-\sBox\s\d+$/);
             if (baseNameMatch) {
                 const baseName = baseNameMatch[1];
                 if (!shipmentGroups[baseName]) {
                     shipmentGroups[baseName] = [];
                 }
                 shipmentGroups[baseName].push(slip);
             } else {
                 standaloneSlips.push(slip);
             }
         });
         
         // Convert groups with multiple boxes to multi-box shipments
         Object.keys(shipmentGroups).forEach(baseName => {
             const boxes = shipmentGroups[baseName];
             if (boxes.length > 1) {
                 // Sort boxes by box number
                 boxes.sort((a, b) => {
                     const aBoxNum = parseInt(a.name.match(/Box\s(\d+)$/)?.[1] || 0);
                     const bBoxNum = parseInt(b.name.match(/Box\s(\d+)$/)?.[1] || 0);
                     return aBoxNum - bBoxNum;
                 });
                 
                 // Calculate receiving status for each box
                 const boxesWithStatus = boxes.map(box => ({
                     ...box,
                     ...getBoxReceiveStatus(box)
                 }));
                 
                 const totalItems = boxes.reduce((sum, box) => sum + getPackingSlipItemCount(box), 0);
                 if (totalItems === 0) {
                     return;
                 }
                 
                 multiBoxShipments.push({
                     id: `shipment-${baseName}`,
                     baseName,
                     boxes: boxesWithStatus,
                     type: 'multi-box-shipment',
                     // Aggregate data for display
                     vendorName: boxes[0].vendorName,
                     dateCreated: boxes[0].dateCreated,
                     trackingNumber: boxes[0].trackingNumber,
                     totalItems,
                     isFullyReceived: boxesWithStatus.every(box => box.isFullyReceived),
                     hasAnyReceiving: boxesWithStatus.some(box => box.isPartiallyReceived || !box.isFullyReceived),
                     dateReceived: boxesWithStatus.every(box => box.dateReceived) ? 
                         Math.max(...boxesWithStatus.map(box => new Date(box.dateReceived))) : null
                 });
             } else {
                 // Single box with naming convention, treat as standalone
                 standaloneSlips.push(boxes[0]);
             }
         });
         
         return { multiBoxShipments, standaloneSlips };
     };

    toggleSection = (sectionName) => {
        this.setState(prevState => ({
            collapsedSections: {
                ...prevState.collapsedSections,
                [sectionName]: !prevState.collapsedSections[sectionName]
            }
        }));
    };

    // Multi-box shipment receiving methods
    handleMultiBoxShipmentSelect = (shipment) => {
        this.setState({
            selectedMultiBoxShipment: shipment
        });
        this.loadAllPurchasedPartsForVendor(shipment.vendorName);
    };

    closeMultiBoxShipmentDialog = () => {
        this.setState({
            selectedMultiBoxShipment: null
        });
    };

    // Get part information for a packing slip item (copied from PackingSlips.js)
    getPartInfo = (item) => {        
        // First try to find the purchased part from outstandingParts
        if (item.purchasedPartId && this.state.outstandingParts && this.state.outstandingParts.length > 0) {
            const purchasedPart = this.state.outstandingParts.find(p => p.id === item.purchasedPartId);
            
            if (purchasedPart) {
                // Look up individual part by partId
                if (purchasedPart.partId && this.state.parts && this.state.parts.length > 0) {
                    const partData = this.state.parts.find(p => p.part && p.part.id === purchasedPart.partId);
                    if (partData?.part) {
                        const revision = partData.part.revision ? ` rev ${partData.part.revision}` : '';
                        return {
                            partId: partData.part.partId + revision,
                            description: partData.part.description
                        };
                    }
                }
                
                // Look up part family by partFamilyId  
                if (purchasedPart.partFamilyId && this.state.partFamilies && this.state.partFamilies.length > 0) {
                    const family = this.state.partFamilies.find(f => f.id === purchasedPart.partFamilyId);
                    if (family) {
                        return {
                            partId: family.partFamilyId,
                            description: family.description
                        };
                    }
                }
                
                // Use part number and description from purchased part if available
                if (purchasedPart.partNumber && purchasedPart.partDescription) {
                    return {
                        partId: purchasedPart.partNumber,
                        description: purchasedPart.partDescription
                    };
                }
                
                // Use part family name if available
                if (purchasedPart.partFamilyName) {
                    return {
                        partId: purchasedPart.partFamilyName,
                        description: purchasedPart.partFamilyName
                    };
                }
            }
        }
        
        // Legacy support: Check if we have the purchased part data directly
        if (item.purchasedPart) {
            // Look up individual part by partId
            if (item.purchasedPart.partId && this.state.parts && this.state.parts.length > 0) {
                const partData = this.state.parts.find(p => p.part && p.part.id === item.purchasedPart.partId);
                if (partData?.part) {
                    const revision = partData.part.revision ? ` rev ${partData.part.revision}` : '';
                    return {
                        partId: partData.part.partId + revision,
                        description: partData.part.description
                    };
                }
            }
            
            // Look up part family by partFamilyId  
            if (item.purchasedPart.partFamilyId && this.state.partFamilies && this.state.partFamilies.length > 0) {
                const family = this.state.partFamilies.find(f => f.id === item.purchasedPart.partFamilyId);
                if (family) {
                    return {
                        partId: family.partFamilyId,
                        description: family.description
                    };
                }
            }
            
            // Check if the purchased part has direct part description
            if (item.purchasedPart.partDescription) {
                return {
                    partId: 'PP-' + item.purchasedPart.id.substring(0, 8),
                    description: item.purchasedPart.partDescription
                };
            }
            
            // Fallback to company name if available
            if (item.purchasedPart.companyName) {
                return {
                    partId: 'VENDOR-' + item.purchasedPart.id.substring(0, 8),
                    description: item.purchasedPart.companyName
                };
            }
        }
        
        // Final fallback
        return {
            partId: 'Unknown',
            description: 'Unknown Part'
        };
    };

    render() {
        if (this.state.loading) {
            return (
                <WorkshopTemplate>
                    <div className="mobile-container">
                        <CircularProgress/>
                    </div>
                </WorkshopTemplate>
            );
        }

        let assemblyOptions = [];

        this.state.assemblyFamilies.forEach((family) => {
            assemblyOptions.push(buildWorkshopOption({
                title: `${family.assemblyFamilyId} 👨‍👩‍👦`,
                value: family.id,
                description: family.description
            }));
        });

        this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
            assemblyOptions.push(buildWorkshopOption({
                title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}${assembly.assembly.currentlyPackaging ? ' 📦' : ''}${isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies) ? ' 🛒' : ''}`,
                value: assembly.assembly.id,
                description: assembly.assembly.description
            }));
        });

        this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((assembly) => {
            assemblyOptions.push(buildWorkshopOption({
                title: `${assembly.subAssembly.subAssemblyId} rev ${assembly.subAssembly.revision}${assembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                value: assembly.subAssembly.id,
                description: assembly.subAssembly.description
            }));
        });

        this.state.parts.filter(x => x.part.isActive).forEach((assembly) => {
            assemblyOptions.push(buildWorkshopOption({
                title: `${assembly.part.partId} rev ${assembly.part.revision}`,
                value: assembly.part.id,
                description: assembly.part.description
            }));
        });

        let assemblyListHtml = [];
        if (this.state.selectedPartOrAssembly) {
            let assemblyExpandedMaybe = this.state.assemblies.find(x => x.assembly.id === this.state.selectedPartOrAssembly);
            let assemblyMaybe = assemblyExpandedMaybe ? this.state.assemblies.find(x => x.assembly.id === this.state.selectedPartOrAssembly).assembly : null;
            let subAssemblyExpandedMaybe = this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedPartOrAssembly);
            let subAssemblyMaybe = subAssemblyExpandedMaybe ? this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedPartOrAssembly).subAssembly : null;
            let partExpandedMaybe = this.state.parts.find(x => x.part.id === this.state.selectedPartOrAssembly);
            let partMaybe = partExpandedMaybe ? this.state.parts.find(x => x.part.id === this.state.selectedPartOrAssembly).part : null;
            let assemblyFamilyMaybe = this.state.assemblyFamilies.find(x => x.id === this.state.selectedPartOrAssembly);

            if (assemblyMaybe) {
                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        <h3>{assemblyMaybe.assemblyId} rev {assemblyMaybe.revision}</h3>
                        <Typography variant="body2" style={{marginTop: -8, marginBottom: 12, color: '#666'}}>
                            {assemblyMaybe.description || 'No description'}
                        </Typography>
                        {assemblyMaybe.assemblyFamilyId ?
                            <div>
                                <Button 
                                    variant="contained"
                                    color="primary"
                                    onClick={() => globalProps.history.push(`/package/family/${assemblyMaybe.assemblyFamilyId}`)}
                                    className="success-button-lg"
                                >
                                    Package Family
                                </Button>
                                <br/><br/>
                            </div>
                            : ""
                        }
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/package/${assemblyMaybe.id}`)}
                            className="success-button-lg"
                            disabled={!assemblyMaybe.currentlyPackaging}
                        >
                            {assemblyMaybe.assemblyFamilyId ? "Package Individual" : "Package"}
                        </Button>
                        <br/><br/>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/unpackage/${assemblyMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Unpackage
                        </Button>
                        <br/><br/>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/bom/${assemblyMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Bill of Materials
                        </Button>
                        <br/><br/>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/sell/${assemblyMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Sell
                        </Button>
                        <br/><br/>
                    </div>
                );
            } else if (subAssemblyMaybe) {
                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        <h3>{subAssemblyMaybe.subAssemblyId} rev {subAssemblyMaybe.revision}</h3>
                        <Typography variant="body2" style={{marginTop: -8, marginBottom: 12, color: '#666'}}>
                            {subAssemblyMaybe.description || 'No description'}
                        </Typography>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/package/${subAssemblyMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Package
                        </Button>
                        <br/><br/>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/unpackage/${subAssemblyMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Unpackage
                        </Button>
                        <br/><br/>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/bom/${subAssemblyMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Bill of Materials
                        </Button>
                        <br/><br/>
                        {this.state.permissions.includes('Purchase') ?
                            <React.Fragment>
                                <Button 
                                    variant="contained"
                                    color="primary"
                                    onClick={() => globalProps.history.push(`/sell/${subAssemblyMaybe.id}`)}
                                    className="success-button-lg"
                                >
                                    Sell
                                </Button>
                                <br/><br/>
                            </React.Fragment>
                        : ""}
                    </div>
                );
            } else if (partMaybe) {
                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        <h3>{partMaybe.partId} rev {partMaybe.revision}</h3>
                        <Typography variant="body2" style={{marginTop: -8, marginBottom: 12, color: '#666'}}>
                            {partMaybe.description || 'No description'}
                        </Typography>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/inventory/${partMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Adjust Inventory
                        </Button>
                        <br/><br/>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/set-inventory/${partMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Set Inventory
                        </Button>
                        <br/><br/>
                        {this.state.permissions.includes('Purchase') ?
                            <React.Fragment>
                                <Button 
                                    variant="contained"
                                    color="primary"
                                    onClick={() => globalProps.history.push(`/sell/${partMaybe.id}`)}
                                    className="success-button-lg"
                                >
                                    Sell
                                </Button>
                                <br/><br/>
                            </React.Fragment>
                        : ""}
                    </div>
                );
            } else if (assemblyFamilyMaybe) {
                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        <h3>{assemblyFamilyMaybe.assemblyFamilyId}</h3>
                        <Typography variant="body2" style={{marginTop: -8, marginBottom: 12, color: '#666'}}>
                            {assemblyFamilyMaybe.description || 'No description'}
                        </Typography>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/package/family/${assemblyFamilyMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Package
                        </Button>
                        <br/><br/>
                        {/*<Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push(`/bom/${assemblyFamilyMaybe.id}`)}
                            className="success-button-lg"
                        >
                            Bill of Materials
                        </Button>
                        <br/><br/>*/}
                    </div>
                );
            }
        }

        return (
            <WorkshopTemplate>
                {this.state.pendingCustomerOrders.length > 0 && (
                    <div className="mobile-container" style={{ maxWidth: 700, marginBottom: 24 }}>
                        <h1 style={{ marginTop: 0, marginBottom: 8 }}>Pending customer orders</h1>
                        {this.state.pendingCustomerOrders.map((order) => (
                            <div
                                key={order.id}
                                style={{
                                    border: '1px solid #ddd',
                                    borderRadius: 8,
                                    padding: 12,
                                    marginBottom: 10,
                                    background: '#fff8e6',
                                }}
                            >
                                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                    {order.orderNumber}
                                    {' '}
                                    · $
                                    {Number(order.amount || 0).toFixed(2)}
                                </Typography>
                                <Typography variant="body2" style={{ color: '#555' }}>
                                    {order.customerName || order.customerEmail}
                                    {' '}
                                    ·
                                    {' '}
                                    {order.lineCount}
                                    {' '}
                                    line(s) ·
                                    {' '}
                                    {formatUtcForDisplay(order.placedAt)}
                                </Typography>
                                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => this.openCustomerOrderFulfill(order)}
                                    >
                                        Fulfill &amp; print
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => globalProps.history.push(`/admin/orders/${order.id}`)}
                                    >
                                        Open in admin
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="mobile-container" style={{maxWidth: 700}}>
                    <h1 style={{marginTop: 0, marginBottom: 5}}>Find A Part / Assembly</h1>
                    <Autocomplete autoHighlight
                        id="ddlAssembly"
                        options={assemblyOptions}
                        onChange={this.handleAssemblyChange}
                        getOptionLabel={(option) => option.title}
                        getOptionSelected={(option) => option.value === this.state.selectedPartOrAssembly}
                        renderOption={(option) => (
                            <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                                <Typography variant="body2" style={{fontWeight: 600, color: '#222'}}>
                                    {option.title}
                                </Typography>
                                {option.description ? (
                                    <Typography variant="caption" style={{color: '#666'}}>
                                        {option.description}
                                    </Typography>
                                ) : null}
                            </div>
                        )}
                        className="autocomplete-dropdown"
                        style={{maxWidth: 600}}
                        renderInput={(params) => <TextField {...params} inputProps={{
                            ...params.inputProps
                        }} label="Search" autoComplete="new-password" variant="outlined"/>}
                    />
                    {assemblyListHtml}
                </div>
                <div className="mobile-container" style={{maxWidth: 700}}>
                    <h1 style={{marginTop: 0, marginBottom: 5}}>Today's Changes</h1>
                    {todaysChanges.inventoryLogs.length === 0 && todaysChanges.salesLogs.length === 0 && todaysChanges.purchaseLogs.length === 0 && todaysChanges.packageLogs.length === 0 ?
                        <div>None</div>
                        :
                        <div>
                            {(() => {
                                // Sort inventory logs by timestamp
                                const sortedInventoryLogs = [...todaysChanges.inventoryLogs].filter(x => x.quantity !== 0).sort((a, b) => {
                                    const dateA = new Date(a.dateCreated || a.logDate);
                                    const dateB = new Date(b.dateCreated || b.logDate);
                                    return dateA - dateB;
                                });
                                
                                // Sort package logs by timestamp
                                const sortedPackageLogs = [...todaysChanges.packageLogs].filter(x => x.quantity !== 0).sort((a, b) => {
                                    const dateA = new Date(a.dateCreated || a.logDate);
                                    const dateB = new Date(b.dateCreated || b.logDate);
                                    return dateA - dateB;
                                });
                                
                                // Sort sales logs by timestamp
                                const sortedSalesLogs = todaysChanges.salesLogs
                                    .filter(x => x.source === 'Customer Service')
                                    .sort((a, b) => {
                                        const dateA = new Date(a.dateCreated || a.logDate);
                                        const dateB = new Date(b.dateCreated || b.logDate);
                                        return dateA - dateB;
                                    });
                                
                                // Sort purchase logs by timestamp
                                const sortedPurchaseLogs = [...todaysChanges.purchaseLogs].sort((a, b) => {
                                    const dateA = new Date(a.orderDate || a.logDate);
                                    const dateB = new Date(b.orderDate || b.logDate);
                                    return dateA - dateB;
                                });
                                
                                                                return (
                                    <React.Fragment>
                                        {sortedInventoryLogs.length > 0 ?
                                            <div>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    marginBottom: '8px',
                                                    justifyContent: 'center'
                                                }} onClick={() => this.toggleSection('inventory')}>
                                                    <IconButton size="small" style={{marginRight: '8px'}}>
                                                        {this.state.collapsedSections.inventory ? <ExpandMore /> : <ExpandLess />}
                                                    </IconButton>
                                                    <h2 className="todays-changes-h2">Inventory Adjustments ({sortedInventoryLogs.length})</h2>
                                                </div>
                                                <Collapse in={!this.state.collapsedSections.inventory}>
                                                    {sortedInventoryLogs.map((inventoryLog, index) => {
                                                        const part = this.state.parts.find(x => x.part.id === inventoryLog.partId);
                                                        const timestamp = new Date(inventoryLog.dateCreated || inventoryLog.logDate);
                                                        const timeString = timestamp.toLocaleTimeString([], { 
                                                            hour: '2-digit', 
                                                            minute: '2-digit'
                                                        });
                                                        
                                                        return part ? (
                                                            <div key={index} style={{marginBottom: 10}}>
                                                                {part.part.partId} rev {part.part.revision}: {inventoryLog.quantity}
                                                                <br />
                                                                <span style={{fontSize: '12px', color: '#666'}}>{timeString}</span>
                                                            </div>
                                                        ) : null;
                                                    })}
                                                </Collapse>
                                            </div>
                                            : ''}
                                        {sortedPackageLogs.length > 0 ?
                                            <div>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    marginBottom: '8px',
                                                    justifyContent: 'center'
                                                }} onClick={() => this.toggleSection('packaged')}>
                                                    <IconButton size="small" style={{marginRight: '8px'}}>
                                                        {this.state.collapsedSections.packaged ? <ExpandMore /> : <ExpandLess />}
                                                    </IconButton>
                                                    <h2 className="todays-changes-h2">Packaged ({sortedPackageLogs.length})</h2>
                                                </div>
                                                <Collapse in={!this.state.collapsedSections.packaged}>
                                                    {sortedPackageLogs.map((packageLog, index) => {
                                                        const assembly = this.state.assemblies.find(x => x.assembly.id === packageLog.assemblyId);
                                                        const subAssembly = this.state.subAssemblies.find(x => x.subAssembly.id === packageLog.subAssemblyId);
                                                        const timestamp = new Date(packageLog.dateCreated || packageLog.logDate);
                                                        const timeString = timestamp.toLocaleTimeString([], { 
                                                            hour: '2-digit', 
                                                            minute: '2-digit'
                                                        });
                                                        
                                                        return (
                                                            <div key={index} style={{marginBottom: 10}}>
                                                                {assembly ? `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}` : `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}`}: {packageLog.quantity}
                                                                <br />
                                                                <span style={{fontSize: '12px', color: '#666'}}>{timeString}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </Collapse>
                                            </div>
                                            : ''}
                                        {sortedSalesLogs.length > 0 ?
                                            <div>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    marginBottom: '8px',
                                                    justifyContent: 'center'
                                                }} onClick={() => this.toggleSection('sales')}>
                                                    <IconButton size="small" style={{marginRight: '8px'}}>
                                                        {this.state.collapsedSections.sales ? <ExpandMore /> : <ExpandLess />}
                                                    </IconButton>
                                                    <h2 className="todays-changes-h2">Manual Sales ({sortedSalesLogs.length})</h2>
                                                </div>
                                                <Collapse in={!this.state.collapsedSections.sales}>
                                                    {sortedSalesLogs.map((salesLog, index) => {
                                                        const assembly = this.state.assemblies.find(x => x.assembly.id === salesLog.assemblyId);
                                                        const part = this.state.parts.find(x => x.part.id === salesLog.partId);
                                                        const timestamp = new Date(salesLog.dateCreated || salesLog.logDate);
                                                        const timeString = timestamp.toLocaleTimeString([], { 
                                                            hour: '2-digit', 
                                                            minute: '2-digit'
                                                        });
                                                        
                                                        if (assembly) {
                                                            return (
                                                                <div key={index} style={{marginBottom: 10}}>
                                                                    {assembly.assembly.assemblyId} rev {assembly.assembly.revision}: {salesLog.quantity}
                                                                    <br />
                                                                    <span style={{fontSize: '12px', color: '#666'}}>{timeString}</span>
                                                                </div>
                                                            );
                                                        } else if (part) {
                                                            return (
                                                                <div key={index} style={{marginBottom: 10}}>
                                                                    {part.part.partId} rev {part.part.revision}: {salesLog.quantity}
                                                                    <br />
                                                                    <span style={{fontSize: '12px', color: '#666'}}>{timeString}</span>
                                                                </div>
                                                            );
                                                        } else {
                                                            return null;
                                                        }
                                                    })}
                                                </Collapse>
                                            </div>
                                            : ''}
                                        {sortedPurchaseLogs.length > 0 ?
                                            <div>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    marginBottom: '8px',
                                                    justifyContent: 'center'
                                                }} onClick={() => this.toggleSection('received')}>
                                                    <IconButton size="small" style={{marginRight: '8px'}}>
                                                        {this.state.collapsedSections.received ? <ExpandMore /> : <ExpandLess />}
                                                    </IconButton>
                                                    <h2 className="todays-changes-h2">Received ({sortedPurchaseLogs.length})</h2>
                                                </div>
                                                <Collapse in={!this.state.collapsedSections.received}>
                                                    {sortedPurchaseLogs.map((purchaseLog, index) => {
                                                        const part = this.state.parts.find(x => x.part.id === purchaseLog.partId);
                                                        const timestamp = new Date(purchaseLog.receivedDate || purchaseLog.logDate);
                                                        const timeString = timestamp.toLocaleTimeString([], { 
                                                            hour: '2-digit', 
                                                            minute: '2-digit'
                                                        });
                                                        
                                                        return part ? (
                                                            <div key={index} style={{marginBottom: 10}}>
                                                                {part.part.partId} rev {part.part.revision}: {purchaseLog.quantityReceived}
                                                                <br />
                                                                <span style={{fontSize: '12px', color: '#666'}}>{timeString}</span>
                                                            </div>
                                                        ) : null;
                                                    })}
                                                </Collapse>
                                            </div>
                                            : ''}
                                    </React.Fragment>
                                );
                            })()}
                        </div>
                    }
                </div>
                <div className="mobile-container" style={{maxWidth: 700}}>
                    <h1 style={{marginTop: 0, marginBottom: 5}}>Packing Slips</h1>
                                         {this.state.packingSlips === null ?
                         <CircularProgress />
                         : <div>
                         {this.state.packingSlips.filter(ps => !this._locallyReceivedPackingSlipIds.has(String(ps.id).toLowerCase())).length === 0 ?
                             <div>No active packing slips</div>
                            :
                            <div>
                                {(() => {
                                    const visiblePackingSlips = this.state.packingSlips.filter(ps =>
                                        !this._locallyReceivedPackingSlipIds.has(String(ps.id).toLowerCase())
                                    );
                                    const { multiBoxShipments, standaloneSlips } = this.detectMultiBoxShipments(visiblePackingSlips);
                                    
                                    // Sort multi-box shipments by date (oldest first)
                                    const sortedMultiBoxShipments = multiBoxShipments.sort((a, b) => {
                                        const dateA = new Date(a.dateCreated);
                                        const dateB = new Date(b.dateCreated);
                                        return dateA - dateB;
                                    });
                                    
                                    // Sort standalone slips by date (oldest first)
                                    const sortedStandaloneSlips = standaloneSlips.sort((a, b) => {
                                        const dateA = new Date(a.dateCreated);
                                        const dateB = new Date(b.dateCreated);
                                        return dateA - dateB;
                                    });
                                    
                                    return (
                                        <React.Fragment>
                                            {/* Multi-box shipments */}
                                            {sortedMultiBoxShipments.map((shipment) => (
                                                <div key={shipment.id} style={{
                                                    border: '1px solid #ddd',
                                                    borderRadius: '8px',
                                                    padding: '12px',
                                                    marginBottom: '12px',
                                                    backgroundColor: '#f9f9f9'
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        marginBottom: '8px'
                                                    }}>
                                                        <div style={{flex: 1}}>
                                                            <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '4px'}}>
                                                                {shipment.baseName} ({shipment.boxes.length} boxes)
                                                            </div>
                                                            <div style={{fontSize: '14px', color: '#666', marginBottom: '2px'}}>
                                                                Vendor: {shipment.vendorName}
                                                            </div>
                                                            <div style={{fontSize: '14px', color: '#666', marginBottom: '2px'}}>
                                                                Total Items: {shipment.totalItems}
                                                            </div>
                                                            {shipment.trackingNumber && (
                                                                <div style={{fontSize: '14px', color: '#666', marginBottom: '2px'}}>
                                                                    Tracking: {shipment.trackingNumber}
                                                                </div>
                                                            )}
                                                            <div style={{fontSize: '12px', color: '#888'}}>
                                                                {formatUtcForDisplay(shipment.dateCreated)}
                                                            </div>
                                                        </div>
                                                        <div style={{display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px'}}>
                                                            <Button
                                                                onClick={() => this.handleMultiBoxShipmentSelect(shipment)}
                                                                className="primary-button-sm success-button-lg"
                                                                style={{fontSize: '12px'}}
                                                            >Receive</Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            
                                                                                         {/* Standalone packing slips */}
                                             {sortedStandaloneSlips.map((packingSlip) => {
                                                 // Calculate receiving status
                                                 const totalExpected = packingSlip.items?.reduce((sum, item) => sum + (item.quantityExpected || 0), 0) || 0;
                                                 const totalReceived = packingSlip.items?.reduce((sum, item) => sum + (item.quantityReceived || 0), 0) || 0;
                                                 const remainingToReceive = totalExpected - totalReceived;
                                                 const isPartiallyReceived = totalReceived > 0 && remainingToReceive > 0;
                                                 const isFullyReceived = totalReceived >= totalExpected;
                                                 
                                                 return (
                                                     <div key={packingSlip.id} style={{
                                                         border: '1px solid #ddd',
                                                         borderRadius: '8px',
                                                         padding: '12px',
                                                         marginBottom: '12px',
                                                         backgroundColor: isPartiallyReceived ? '#fff3cd' : '#f9f9f9'
                                                     }}>
                                                         <div style={{
                                                             display: 'flex',
                                                             justifyContent: 'space-between',
                                                             alignItems: 'flex-start',
                                                             marginBottom: '8px'
                                                         }}>
                                                             <div style={{flex: 1}}>
                                                                 <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '4px'}}>
                                                                     {packingSlip.name || 'Unnamed'}
                                                                 </div>
                                                                 <div style={{fontSize: '14px', color: '#666', marginBottom: '2px'}}>
                                                                     Vendor: {packingSlip.vendorName}
                                                                 </div>
                                                                 <div style={{fontSize: '14px', color: '#666', marginBottom: '2px'}}>
                                                                     Items: {packingSlip.items?.length || 0}
                                                                 </div>
                                                                 {packingSlip.trackingNumber && (
                                                                     <div style={{fontSize: '14px', color: '#666', marginBottom: '2px'}}>
                                                                         Tracking: {packingSlip.trackingNumber}
                                                                     </div>
                                                                 )}
                                                                 <div style={{fontSize: '12px', color: '#888'}}>
                                                                     {formatUtcForDisplay(packingSlip.dateCreated)}
                                                                 </div>
                                                             </div>
                                                             <div style={{display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px'}}>
                                                                 <Button
                                                                     onClick={() => this.handlePackingSlipSelect(packingSlip)}
                                                                     className="primary-button-sm success-button-lg"
                                                                     style={{fontSize: '12px'}}
                                                                 >Receive</Button>
                                                             </div>
                                                         </div>
                                                     </div>
                                                 );
                                             })}
                                        </React.Fragment>
                                    );
                                })()}
                            </div>}
                        </div>}
                    </div>

                {/* Packing Slip Receive Dialog */}
                {this.state.selectedPackingSlip && (
                    <Dialog 
                        open={true} 
                        onClose={this.closePackingSlipDialog} 
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>
                            Receive Packing Slip
                            <Typography variant="subtitle2" style={{color: '#666'}}>
                                {this.state.selectedPackingSlip.name || 'Unnamed'} - {this.state.selectedPackingSlip.vendorName}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <div style={{marginBottom: '16px'}}>
                                 <Button
                                     onClick={this.handleReceiveAll}
                                     className="success-button-lg"
                                     disabled={this.state.receivingPackingSlip}
                                     style={{width: '100%', marginBottom: '12px'}}
                                 >Receive All Remaining</Button>
                             </div>
                            
                            <Typography variant="subtitle1" style={{marginBottom: '8px'}}>
                                Or adjust quantities:
                            </Typography>
                            
                                                         {this.state.selectedPackingSlip.items?.sort((a, b) => {
                                                             const partInfoA = this.getPartInfo(a);
                                                             const partInfoB = this.getPartInfo(b);
                                                             return partInfoA.partId.localeCompare(partInfoB.partId);
                                                         }).map((item) => {
                                 const remainingToReceive = Math.max(0, item.quantityExpected - (item.quantityReceived || 0));
                                 const isPartiallyReceived = (item.quantityReceived || 0) > 0;
                                 
                                 return (
                                     <div key={item.id} style={{
                                         display: 'flex',
                                         justifyContent: 'space-between',
                                         alignItems: 'center',
                                         padding: '8px 0',
                                         borderBottom: '1px solid #eee'
                                     }}>
                                         <div style={{flex: 1, marginRight: '12px', flexBasis: '100%'}}>
                                             <div style={{fontSize: '14px', fontWeight: 'bold'}}>
                                                  {this.getPartInfo(item).partId}
                                             </div>
                                             <div style={{fontSize: '12px', color: '#666'}}>
                                                 Expected: {item.quantityExpected}
                                                 {isPartiallyReceived && (
                                                     <div>
                                                         Received: {item.quantityReceived || 0}
                                                     </div>
                                                 )}
                                             </div>
                                         </div>
                                                                                   <TextField
                                              type="number"
                                              value={this.state.receivedQuantities[item.id] === 0 ? '' : this.state.receivedQuantities[item.id] || ''}
                                              onChange={(e) => this.handleQuantityChange(item.id, e.target.value)}
                                              inputProps={{min: 0, max: remainingToReceive}}
                                              variant="outlined"
                                              size="small"
                                              autoComplete="off"
                                          />
                                     </div>
                                 );
                             })}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={this.closePackingSlipDialog}>
                                Cancel
                            </Button>
                            <Button
                                onClick={this.handleReceiveCustom}
                                variant="contained"
                                color="primary"
                                disabled={this.state.receivingPackingSlip}
                            >
                                {this.state.receivingPackingSlip ? <CircularProgress size={20} /> : 'Receive'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}

                {/* Multi-Box Shipment Receive Drawer */}
                {this.state.selectedMultiBoxShipment && (
                    <DrawerMenuRight
                        open={true}
                        onClose={this.closeMultiBoxShipmentDialog}
                        title="Receive Multi-Box Shipment"
                        subtitle={`${this.state.selectedMultiBoxShipment.baseName} - ${this.state.selectedMultiBoxShipment.vendorName}`}
                    >
                        <div style={{padding: '0 16px'}}>
                            <div style={{marginBottom: '20px'}}>
                                                                 <Typography variant="h6" style={{marginBottom: '16px', fontSize: '18px'}}>
                                      Boxes ({this.state.selectedMultiBoxShipment.boxes.filter(box => !box.isFullyReceived && getPackingSlipItemCount(box) > 0).length})
                                  </Typography>
                                
                                                                 {this.state.selectedMultiBoxShipment.boxes
                                      .filter(box => !box.isFullyReceived && getPackingSlipItemCount(box) > 0)
                                      .map((box, boxIndex) => (
                                        <div key={box.id} style={{
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            marginBottom: '16px',
                                            backgroundColor: box.isPartiallyReceived ? '#fff3cd' : '#f9f9f9'
                                        }}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                            gap: '12px'
                                        }}>
                                            <div style={{
                                                alignItems: 'flex-start'
                                            }}>
                                                <div style={{flex: 1}}>
                                                    <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '4px'}}>
                                                        {box.name}
                                                    </div>
                                                    <div style={{fontSize: '14px', color: '#666'}}>
                                                        Items: {box.items?.length || 0}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div style={{
                                                 justifyContent: 'flex-end'
                                             }}>
                                                <Button
                                                    onClick={() => {
                                                        this.handlePackingSlipSelect(box);
                                                    }}
                                                    className="primary-button-sm success-button-lg"
                                                    disabled={this.state.receivingPackingSlip}
                                                    style={{
                                                        fontSize: '14px',
                                                        padding: '12px'
                                                    }}
                                                >
                                                    Receive
                                                </Button>
                                             </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </DrawerMenuRight>
                )}

                <Dialog open={this.state.customerOrderFulfillOpen} onClose={this.closeCustomerOrderFulfill} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        Fulfill
                        {' '}
                        {this.state.selectedCustomerOrder ? this.state.selectedCustomerOrder.orderNumber : 'order'}
                    </DialogTitle>
                    <DialogContent>
                        {this.state.selectedCustomerOrder && (
                            <Typography variant="body2" style={{ marginBottom: 12 }}>
                                {this.state.selectedCustomerOrder.shippingMethodName || 'Shipping method not set'}
                                {this.state.selectedCustomerOrder.shippingCarrierCode
                                    ? ` (${this.state.selectedCustomerOrder.shippingCarrierCode} / ${this.state.selectedCustomerOrder.shippingServiceCode})`
                                    : ''}
                            </Typography>
                        )}
                        <TextField
                            label="Name"
                            fullWidth
                            value={this.state.customerOrderShippingForm.customerName}
                            onChange={(e) => this.setCustomerOrderShippingField('customerName', e.target.value)}
                            style={{ marginBottom: 8 }}
                        />
                        <TextField
                            label="Street"
                            fullWidth
                            value={this.state.customerOrderShippingForm.line1}
                            onChange={(e) => this.setCustomerOrderShippingField('line1', e.target.value)}
                            style={{ marginBottom: 8 }}
                        />
                        <TextField
                            label="Apt / suite"
                            fullWidth
                            value={this.state.customerOrderShippingForm.line2}
                            onChange={(e) => this.setCustomerOrderShippingField('line2', e.target.value)}
                            style={{ marginBottom: 8 }}
                        />
                        <TextField
                            label="City"
                            fullWidth
                            value={this.state.customerOrderShippingForm.city}
                            onChange={(e) => this.setCustomerOrderShippingField('city', e.target.value)}
                            style={{ marginBottom: 8 }}
                        />
                        <FormControl fullWidth style={{ marginBottom: 8 }}>
                            <InputLabel>Country</InputLabel>
                            <Select
                                value={this.state.customerOrderShippingForm.country || ''}
                                onChange={(e) => this.setCustomerOrderShippingField('country', e.target.value)}
                            >
                                {COUNTRIES.map((country) => (
                                    <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {isUnitedStates(this.state.customerOrderShippingForm.country) && (
                            <FormControl fullWidth style={{ marginBottom: 8 }}>
                                <InputLabel>State</InputLabel>
                                <Select
                                    value={this.state.customerOrderShippingForm.stateOrProvince || ''}
                                    onChange={(e) => this.setCustomerOrderShippingField('stateOrProvince', e.target.value)}
                                >
                                    {US_STATES.map((state) => (
                                        <MenuItem key={state.code} value={state.code}>{state.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <TextField
                            label="Postal code"
                            fullWidth
                            value={this.state.customerOrderShippingForm.postalCode}
                            onChange={(e) => this.setCustomerOrderShippingField('postalCode', e.target.value)}
                            style={{ marginBottom: 8 }}
                        />
                        <Button onClick={this.saveCustomerOrderShippingAddress} style={{ marginBottom: 16 }}>Save address</Button>
                        <TextField
                            label="Package weight (oz)"
                            type="number"
                            fullWidth
                            value={this.state.customerOrderWeightOz}
                            onChange={(e) => this.setState({ customerOrderWeightOz: e.target.value })}
                            style={{ marginBottom: 12 }}
                        />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={this.state.customerOrderAutoPackage}
                                    onChange={(e) => this.setState({ customerOrderAutoPackage: e.target.checked })}
                                    color="primary"
                                />
                            )}
                            label="Auto-package assembly/subassembly lines"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeCustomerOrderFulfill}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={this.state.customerOrderFulfilling}
                            onClick={this.fulfillCustomerOrder}
                        >
                            {this.state.customerOrderFulfilling ? <CircularProgress size={20} /> : 'Fulfill & print label'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </WorkshopTemplate>
        );
    }
}

let globalProps;

Search.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Search);
